import type { TeamAssignment } from '../api/client';

/**
 * Cómo va una lectura obligatoria, y cómo va un conjunto de ellas.
 *
 * Es la misma regla que usa la API para el Excel de gerencia
 * (`domain/identity/gerencia.ts`): leído es haber acusado; vencido, no haberlo
 * hecho a tiempo. Se repite aquí porque la pantalla agrupa por persona y por
 * documento sin volver a preguntarle al servidor cada vez que cambia un filtro.
 */
export type EstadoDeLectura = 'LEIDO' | 'PENDIENTE' | 'VENCIDO';

export function estadoDe(lectura: Pick<TeamAssignment, 'acknowledgedAt' | 'daysLeft'>): EstadoDeLectura {
  if (lectura.acknowledgedAt) return 'LEIDO';
  return lectura.daysLeft < 0 ? 'VENCIDO' : 'PENDIENTE';
}

export interface Resumen {
  total: number;
  leidos: number;
  pendientes: number;
  vencidos: number;
  /** Entre 0 y 1. Sin nada obligatorio el avance es completo: no se debe nada. */
  avance: number;
}

export function resumir(lecturas: readonly Pick<TeamAssignment, 'acknowledgedAt' | 'daysLeft'>[]): Resumen {
  let leidos = 0;
  let vencidos = 0;
  for (const lectura of lecturas) {
    const estado = estadoDe(lectura);
    if (estado === 'LEIDO') leidos += 1;
    if (estado === 'VENCIDO') vencidos += 1;
  }
  const total = lecturas.length;
  return { total, leidos, pendientes: total - leidos, vencidos, avance: total === 0 ? 1 : leidos / total };
}

/** Agrupa conservando el orden en que aparecen. */
export function agruparPor<T>(elementos: readonly T[], llave: (e: T) => string): Map<string, T[]> {
  const grupos = new Map<string, T[]>();
  for (const elemento of elementos) {
    const clave = llave(elemento);
    const lista = grupos.get(clave);
    if (lista) lista.push(elemento);
    else grupos.set(clave, [elemento]);
  }
  return grupos;
}

/** El tono de una barra de avance: vencido manda sobre todo lo demás. */
export function tonoDe(resumen: Resumen): 'bueno' | 'aviso' | 'critico' {
  if (resumen.vencidos > 0) return 'critico';
  return resumen.pendientes > 0 ? 'aviso' : 'bueno';
}
